import React, { useMemo, useState } from "react";

import { useTranslation } from "utils";

import { CropsWithMissingHistoricalPracticeDataByYear, PRACTICE_SLUG, ZERO_UUID } from "models";

import { ExtraInfo, Flex, IconLinkButton, ProgressBar, Typography } from "components";

type PracticeStats = {
  isSeedingRequired: boolean;
  isTillageRequired: boolean;
  isOrganicFertilizerRequired: boolean;
  completedGrowingPeriod: number;
  completedSeeding: number;
  completedTillage: number;
  completedOrganicFertilizer: number;
};

export const ProgressTile = ({
  cropsWithMissingHistoricalPracticeDataByYear,
  availableYears,
  progressBarColors,
}: {
  cropsWithMissingHistoricalPracticeDataByYear: CropsWithMissingHistoricalPracticeDataByYear;
  availableYears: number[];
  progressBarColors: { progressColor: string; trailColor: string };
}) => {
  const { t } = useTranslation("historicalPracticeDataTableView");

  const [showProgressInfoDrawer, setShowProgressInfoDrawer] = useState(false);

  const stats = useMemo(() => {
    const yearEntries = Object.values(cropsWithMissingHistoricalPracticeDataByYear);
    const cropCount = yearEntries.flat().length;

    const initialPracticeStats: PracticeStats = {
      isSeedingRequired: false,
      isTillageRequired: false,
      isOrganicFertilizerRequired: false,
      completedGrowingPeriod: 0,
      completedSeeding: 0,
      completedTillage: 0,
      completedOrganicFertilizer: 0,
    };

    const { practiceStats, completedYears } = yearEntries.reduce(
      (acc, entries) => {
        const yearPracticeStats = entries.reduce(
          (yearAcc, crop) => {
            const { seedings, tillages, organicFertilizers } = crop.assignedPractices;
            const { isTillageDepthRequired } = crop;
            const isMissingGrowingPeriodDates = crop.crop.seedingStartDate === null || crop.crop.harvestEndDate === null;
            const hasMissingTillageDepth =
              isTillageDepthRequired &&
              tillages !== null &&
              tillages.length > 0 &&
              tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);
            const hasMissingTillageEndDate =
              tillages !== null && tillages.length > 0 && tillages.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
            const hasMissingOrganicFertilizerEndDate =
              organicFertilizers !== null &&
              organicFertilizers.length > 0 &&
              organicFertilizers.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
            return {
              isSeedingRequired: yearAcc.isSeedingRequired || seedings !== null,
              isTillageRequired: yearAcc.isTillageRequired || tillages !== null,
              isOrganicFertilizerRequired: yearAcc.isOrganicFertilizerRequired || organicFertilizers !== null,
              completedGrowingPeriod: yearAcc.completedGrowingPeriod + (isMissingGrowingPeriodDates ? 0 : 1),
              completedSeeding: yearAcc.completedSeeding + (seedings !== null && seedings.length > 0 ? 1 : 0),
              completedTillage:
                yearAcc.completedTillage + (tillages !== null && tillages.length > 0 && !hasMissingTillageDepth && !hasMissingTillageEndDate ? 1 : 0),
              completedOrganicFertilizer:
                yearAcc.completedOrganicFertilizer +
                (organicFertilizers !== null && organicFertilizers.length > 0 && !hasMissingOrganicFertilizerEndDate ? 1 : 0),
            };
          },
          { ...initialPracticeStats } as PracticeStats
        );

        const hasCompletedGrowingPeriod = entries.every((e) => e.crop.seedingStartDate !== null && e.crop.harvestEndDate !== null);
        const hasCompletedSeeding =
          !yearPracticeStats.isSeedingRequired || entries.every((e) => e.assignedPractices.seedings !== null && e.assignedPractices.seedings.length > 0);
        const hasCompletedTillage =
          !yearPracticeStats.isTillageRequired ||
          entries.every((e) => {
            const { tillages } = e.assignedPractices;
            if (tillages === null || tillages.length === 0) return false;
            const hasMissingDepth =
              e.isTillageDepthRequired && tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);
            const hasMissingEndDate = tillages.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
            return !hasMissingDepth && !hasMissingEndDate;
          });
        const hasCompletedOrganicFertilizer =
          !yearPracticeStats.isOrganicFertilizerRequired ||
          entries.every((e) => {
            const { organicFertilizers } = e.assignedPractices;
            if (organicFertilizers === null || organicFertilizers.length === 0) return false;
            const hasMissingEndDate = organicFertilizers.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
            return !hasMissingEndDate;
          });

        const yearComplete = hasCompletedGrowingPeriod && hasCompletedSeeding && hasCompletedTillage && hasCompletedOrganicFertilizer;

        return {
          practiceStats: {
            isSeedingRequired: acc.practiceStats.isSeedingRequired || yearPracticeStats.isSeedingRequired,
            isTillageRequired: acc.practiceStats.isTillageRequired || yearPracticeStats.isTillageRequired,
            isOrganicFertilizerRequired: acc.practiceStats.isOrganicFertilizerRequired || yearPracticeStats.isOrganicFertilizerRequired,
            completedGrowingPeriod: acc.practiceStats.completedGrowingPeriod + yearPracticeStats.completedGrowingPeriod,
            completedSeeding: acc.practiceStats.completedSeeding + yearPracticeStats.completedSeeding,
            completedTillage: acc.practiceStats.completedTillage + yearPracticeStats.completedTillage,
            completedOrganicFertilizer: acc.practiceStats.completedOrganicFertilizer + yearPracticeStats.completedOrganicFertilizer,
          },
          completedYears: acc.completedYears + (yearComplete ? 1 : 0),
        };
      },
      { practiceStats: { ...initialPracticeStats }, completedYears: 0 }
    );

    const totalRequired =
      cropCount + // growing period is always required
      (practiceStats.isSeedingRequired ? cropCount : 0) +
      (practiceStats.isTillageRequired ? cropCount : 0) +
      (practiceStats.isOrganicFertilizerRequired ? cropCount : 0);

    const completedGrowingPeriod = practiceStats.completedGrowingPeriod ?? 0;
    const completedSeeding = practiceStats.isSeedingRequired ? practiceStats.completedSeeding : 0;
    const completedTillage = practiceStats.isTillageRequired ? practiceStats.completedTillage : 0;
    const completedOrganicFertilizer = practiceStats.isOrganicFertilizerRequired ? practiceStats.completedOrganicFertilizer : 0;
    const totalCompleted = completedGrowingPeriod + completedSeeding + completedTillage + completedOrganicFertilizer;

    return {
      years: { total: availableYears.length, completed: completedYears },
      totalRequired,
      totalCompleted,
      growingPeriod: {
        isRequired: true,
        total: cropCount,
        completed: completedGrowingPeriod,
      },
      [PRACTICE_SLUG.SEEDINGS]: {
        isRequired: practiceStats.isSeedingRequired,
        total: cropCount,
        completed: completedSeeding,
      },
      [PRACTICE_SLUG.TILLAGES]: {
        isRequired: practiceStats.isTillageRequired,
        total: cropCount,
        completed: completedTillage,
      },
      [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: {
        isRequired: practiceStats.isOrganicFertilizerRequired,
        total: cropCount,
        completed: completedOrganicFertilizer,
      },
    };
  }, [cropsWithMissingHistoricalPracticeDataByYear, availableYears]);

  const progressPercent = stats.totalRequired > 0 ? Math.round((stats.totalCompleted / stats.totalRequired) * 100) : 100;

  const PROGRESS_ITEM_KEYS = ["growingPeriod", PRACTICE_SLUG.SEEDINGS, PRACTICE_SLUG.TILLAGES, PRACTICE_SLUG.ORGANIC_FERTILIZERS] as const;

  return (
    <Flex className="historical-practice-data-table-view-side-panel-progress-container" vertical gap="1.5rem">
      <Flex gap="0.5rem" align="center">
        <Typography variant="h5">{t("sidePanel.progress.title")}</Typography>
        <IconLinkButton
          className="historical-practice-data-table-view-side-panel-progress-info-icon-button"
          theme="grey"
          iconType="info"
          size="small"
          onClick={() => setShowProgressInfoDrawer(true)}
        />
      </Flex>
      <Flex vertical>
        <Flex justify="flex-start" align="center" gap="0.25rem">
          <Typography variant="h1">{progressPercent}</Typography>
          <Typography variant="h3">{t("sidePanel.progress.percentComplete")}</Typography>
        </Flex>
        <ProgressBar
          percent={progressPercent}
          type="line"
          showInfo={false}
          strokeColor={progressBarColors.progressColor}
          trailColor={progressBarColors.trailColor}
          strokeWidth={4}
        />
      </Flex>
      <Flex vertical>
        <Flex justify="space-between" align="center">
          <Typography variant="paragraph-s">{t("sidePanel.progress.years")}</Typography>
          <Typography variant="paragraph-m">
            {stats.years.completed} / {stats.years.total}
          </Typography>
        </Flex>
        {PROGRESS_ITEM_KEYS.map((key) => {
          const { total, completed, isRequired } = stats[key];
          if (!isRequired) return null;

          return <PracticeStats key={key} total={total} completed={completed} label={t(`table.header.${key}`)} />;
        })}
      </Flex>
      <ExtraInfo setVisible={setShowProgressInfoDrawer} type="historical-practice-data-table-view-progress-info" visible={showProgressInfoDrawer} />
    </Flex>
  );
};

const PracticeStats = ({ total, completed, label }: { total: number; completed: number; label: string }) => (
  <Flex vertical>
    <span className="historical-practice-data-table-view-side-panel-stats-divider" />
    <Flex justify="space-between" align="center">
      <Typography variant="paragraph-s">{label}</Typography>
      <Typography variant="paragraph-m">
        {completed} / {total}
      </Typography>
    </Flex>
  </Flex>
);
